/**
 * WDModel - Transparent Reactive Data Model
 * 
 * A redesigned proxy-based reactive system that prioritizes transparency and simplicity
 * while adding performance features like batching and event suspension.
 * 
 * Key design principles:
 * - Constructor returns the proxy itself for transparent object/array replacement
 * - WDModelArray uses native JavaScript array with proxy wrapper (no custom array class)
 * - Event suspension uses simple private variables (no separate event class needed)
 * - Supports recursion and event bubbling like WDModel
 * - Proper set, delete, and change events
 * - Batch operations that suppress individual events
 */

import WDUtil from './wd-util.js';

/**
 * WDModel - Reactive object proxy
 */
export class WDModel
{

  __proxy = null;
  __subscribers = { set: {}, delete: {}, change: {}, batch: {} };
  __eventsSuspended = false;
  __batchMode = false;
  __batchEvents = [];
  __isRecursive = true;
  __bubbleEvents = false;
  __parent = null;
  __parentPath = '';
  __debugName = null;

  constructor(obj = {}, options = {})
  {
    // Handle options
    this.__isRecursive = options.recursive ?? true;
    this.__bubbleEvents = options.bubble ?? false;
    
    // Set debug name if provided, otherwise generate short UUID
    if (options.debugName) {
      this.__debugName = options.debugName;
    } else {
      // Generate 8-character UUID
      this.__debugName = Math.random().toString(36).substring(2, 10).toUpperCase();
    }

    // Create the proxy on the target object/array
    this.__proxy = new Proxy(obj, this);

    // Return the proxy itself for transparent usage
    return this.__proxy;
  }

  /**
   * Proxy trap: get property
   */
  get(target, prop, receiver)
  {
    // Access to internal properties
    if (typeof target[prop] !== 'undefined')
    {
      // If recursive mode and property is an object/array, make it reactive
      if (this.__isRecursive &&
        target[prop] !== null &&
        typeof target[prop] === 'object' &&
        !target[prop]?.isProxy)
      {
        this.__makeChildReactive(target, prop);
      }

      return Reflect.get(target, prop, receiver);
    }
    // Access to handler methods
    else if (typeof this[prop] !== 'undefined')
    {
      return this[prop];
    }
    // Special property to identify proxies
    else if (prop === 'isProxy')
    {
      return true;
    }
    else
    {
      return undefined;
    }
  }

  /**
   * Proxy trap: set property
   */
  set(target, prop, value, receiver)
  {
    // Don't emit events for private properties (starting with __)
    // Set them directly on the handler, not the target
    if (prop.startsWith('__'))
    {
      this[prop] = value;
      return true;
    }

    const oldValue = target[prop];

    // Process value (make reactive if needed)
    const processedValue = this.__processValue(value, prop);

    // Set the value
    const result = Reflect.set(target, prop, processedValue, receiver);

    // Emit set event (always)
    this.__emit('set', prop, {
      property: prop,
      value: processedValue,
      oldValue: oldValue,
      target: this.__proxy
    });

    // Emit change event (only if value actually changed)
    if (!Object.is(oldValue, processedValue))
    {
      this.__emit('change', prop, {
        property: prop,
        value: processedValue,
        oldValue: oldValue,
        target: this.__proxy
      });

      // Bubble to parent if enabled
      if (this.__bubbleEvents && this.__parent)
      {
        const fullPath = this.__parentPath ? `${this.__parentPath}.${prop}` : prop;
        this.__parent.__emit('change', fullPath, {
          property: fullPath,
          value: processedValue,
          oldValue: oldValue,
          target: this.__parent
        });
      }
    }

    return result;
  }

  /**
   * Proxy trap: delete property
   */
  deleteProperty(target, prop)
  {
    if (prop.startsWith('__'))
    {
      throw new Error("Access denied to private properties");
    }

    const oldValue = target[prop];
    delete target[prop];

    // Emit delete event
    this.__emit('delete', prop, {
      property: prop,
      oldValue: oldValue,
      target: this.__proxy
    });

    // Also emit change event for consistency
    this.__emit('change', prop, {
      property: prop,
      value: undefined,
      oldValue: oldValue,
      target: this.__proxy
    });

    // Bubble to parent if enabled
    if (this.__bubbleEvents && this.__parent)
    {
      const fullPath = this.__parentPath ? `${this.__parentPath}.${prop}` : prop;
      this.__parent.__emit('change', fullPath, {
        property: fullPath,
        value: undefined,
        oldValue: oldValue,
        target: this.__parent
      });
    }

    return true;
  }

  /**
   * Proxy trap: list properties (filter out private __ properties)
   */
  ownKeys(target)
  {
    return Object.keys(target).filter(key => !key.startsWith('__'));
  }

  /**
   * Process a value before setting - make reactive if needed
   */
  __processValue(value, prop)
  {
    // Only process in recursive mode
    if (!this.__isRecursive || value === null || typeof value !== 'object')
    {
      return value;
    }

    // Don't re-wrap if already a proxy
    if (value?.isProxy)
    {
      return value;
    }

    // Create reactive child
    let child;
    if (Array.isArray(value))
    {
      child = new WDModelArray(value, {
        recursive: this.__isRecursive,
        bubble: this.__bubbleEvents
      });
    } else if (value.constructor === Object)
    {
      child = new WDModel(value, {
        recursive: this.__isRecursive,
        bubble: this.__bubbleEvents
      });
    } else
    {
      // Don't wrap custom class instances
      return value;
    }

    // Set up parent relationship
    child.__parent = this.__proxy;
    child.__parentPath = prop;

    return child;
  }

  /**
   * Compare two values for equality
   * Uses Object.is for primitives and deep comparison for objects/arrays
   */
  __valuesEqual(oldValue, newValue)
  {
    // Fast path: reference equality (handles primitives, null, undefined)
    if (Object.is(oldValue, newValue))
    {
      // Same reference, but if it's a proxy, check if contents changed
      if (oldValue?.isProxy && newValue?.isProxy)
      {
        // Both are proxies and same reference - compare serialized versions
        // This catches cases like: model.arr = model.arr (after arr was modified)
        return this.__deepEqual(oldValue, newValue);
      }
      return true;
    }

    // Different references - check if both are objects/arrays
    if (oldValue !== null && newValue !== null && 
        typeof oldValue === 'object' && typeof newValue === 'object')
    {
      return this.__deepEqual(oldValue, newValue);
    }

    // Different primitives or one is object and one isn't
    return false;
  }

  /**
   * Deep equality comparison for objects and arrays
   * Handles WDModel/WDModelArray proxies by comparing their serialized forms
   */
  __deepEqual(a, b)
  {
    // Handle proxy objects - serialize them for comparison
    const aData = a?.isProxy ? (Array.isArray(a) ? a.toArray() : a.toObject()) : a;
    const bData = b?.isProxy ? (Array.isArray(b) ? b.toArray() : b.toObject()) : b;

    // Quick checks
    if (aData === bData) return true;
    if (aData == null || bData == null) return false;
    if (typeof aData !== 'object' || typeof bData !== 'object') return aData === bData;

    // Array comparison
    if (Array.isArray(aData) && Array.isArray(bData))
    {
      if (aData.length !== bData.length) return false;
      return aData.every((val, idx) => this.__deepEqual(val, bData[idx]));
    }

    // Array vs non-array
    if (Array.isArray(aData) !== Array.isArray(bData)) return false;

    // Object comparison
    const aKeys = Object.keys(aData);
    const bKeys = Object.keys(bData);

    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every(key => 
      bKeys.includes(key) && this.__deepEqual(aData[key], bData[key])
    );
  }

  /**
   * Make an existing child property reactive
   */
  __makeChildReactive(target, prop)
  {
    const value = target[prop];
    const childProxy = this.__processValue(value, prop);

    // Only replace if we created a new proxy
    if (childProxy !== value)
    {
      target[prop] = childProxy;
    }
  }

  /**
   * Emit an event to subscribers
   */
  __emit(eventType, prop, data)
  {
    // Don't emit if events are suspended
    if (this.__eventsSuspended)
    {
      return;
    }

    // Emit to specific property listeners
    this.__notifySubscribers(eventType, prop, data);

    // Emit to wildcard listeners
    this.__notifySubscribers(eventType, '__all', data);
  }

  /**
   * Notify subscribers for a specific event type and property
   */
  __notifySubscribers(eventType, prop, data)
  {
    const subscribers = this.__subscribers[eventType];
    if (!subscribers || !subscribers[prop])
    {
      return;
    }

    subscribers[prop].forEach(handler =>
    {
      try
      {
        handler(data);
      } catch (err)
      {
        console.error('WDModel event handler error for prop', prop, err);
      }
    });
  }

  // Public API Methods

  /**
   * Subscribe to events
   * @param {string} event - Event type(s), space-delimited: 'set', 'delete', 'change', 'batch'
   * @param {string|function} propOrHandler - Property name or handler function
   * @param {function} handler - Handler function (if prop specified)
   */
  on(event, propOrHandler, handler)
  {
    // Handle: on('set', handler)
    if (typeof propOrHandler === 'function')
    {
      handler = propOrHandler;
      propOrHandler = '__all';
    }

    // Support space-delimited events: 'set change delete'
    const events = event.trim().split(/\s+/);

    events.forEach(eventType =>
    {
      if (!this.__subscribers[eventType])
      {
        this.__subscribers[eventType] = {};
      }

      if (!this.__subscribers[eventType][propOrHandler])
      {
        this.__subscribers[eventType][propOrHandler] = [];
      }

      this.__subscribers[eventType][propOrHandler].push(handler);
    });

    // Return unsubscribe function
    return () => this.off(event, propOrHandler, handler);
  }

  /**
   * Unsubscribe from events
   */
  off(event, propOrHandler, handler)
  {
    if (typeof propOrHandler === 'function')
    {
      handler = propOrHandler;
      propOrHandler = '__all';
    }

    const events = event.trim().split(/\s+/);

    events.forEach(eventType =>
    {
      if (!this.__subscribers[eventType] || !this.__subscribers[eventType][propOrHandler])
      {
        return;
      }

      if (typeof handler === 'undefined')
      {
        // Remove all handlers for this property
        delete this.__subscribers[eventType][propOrHandler];
      } else
      {
        // Remove specific handler
        const handlers = this.__subscribers[eventType][propOrHandler];
        const idx = handlers.indexOf(handler);
        if (idx !== -1)
        {
          handlers.splice(idx, 1);
        }
      }
    });
  }

  /**
   * Shorthand for onChange
   */
  onChange(propOrHandler, handler)
  {
    return this.on('change', propOrHandler, handler);
  }

  /**
   * Shorthand for onSet
   */
  onSet(propOrHandler, handler)
  {
    return this.on('set', propOrHandler, handler);
  }

  /**
   * Shorthand for onDelete
   */
  onDelete(propOrHandler, handler)
  {
    return this.on('delete', propOrHandler, handler);
  }

  /**
   * Execute a function with events suspended
   */
  suspend(fn)
  {
    this.__eventsSuspended = true;
    try
    {
      return fn(this.__proxy);
    } finally
    {
      this.__eventsSuspended = false;
    }
  }

  /**
   * Execute a function in batch mode
   * Individual events are suppressed, batch event fired at the end
   */
  batch(fn)
  {
    //just a simple batch mode that supresses events and sends a batch notification
    const result = this.suspend(fn);
    this.__notifySubscribers('batch', '__all', result);
    return result;
  }

  /**
   * Set recursive mode
   */
  setRecursive(enabled)
  {
    this.__isRecursive = enabled;
    return this.__proxy;
  }

  /**
   * Check if recursive mode enabled
   */
  isRecursive()
  {
    return this.__isRecursive;
  }

  /**
   * Check if this is a proxy
   */
  isProxy()
  {
    return true;
  }

  /**
   * Set debug name for logging
   */
  debugName(name)
  {
    this.__debugName = name;
    return this.__proxy;
  }

  /**
   * Get debug name
   */
  getDebugName()
  {
    return this.__debugName;
  }

  /**
   * Convert to plain object
   */
  toObject()
  {
    const result = {};

    Object.keys(this.__proxy).forEach(key =>
    {
      const value = this.__proxy[key];

      if (value === null)
      {
        result[key] = null;
      } else if (typeof value === 'object')
      {
        if (value?.isProxy)
        {
          result[key] = Array.isArray(value) ? value.toArray() : value.toObject();
        } else
        {
          result[key] = Array.isArray(value) ? [...value] : { ...value };
        }
      } else
      {
        result[key] = value;
      }
    });

    return result;
  }

  /**
   * Assign data (shallow merge)
   */
  assign(data)
  {
    return this.batch(() =>
    {
      Object.assign(this.__proxy, data);
      return this.__proxy;
    });
  }

  /**
   * Merge data (deep merge)
   */
  merge(data)
  {
    return this.batch(() =>
    {
      WDUtil.merge(this.__proxy, data);
      return this.__proxy;
    });
  }

  /**
   * Replace all data
   */
  replace(data)
  {
    return this.batch(() =>
    {
      const keepKeys = Object.keys(data);

      // Remove keys not in new data
      Object.keys(this.__proxy).forEach(key =>
      {
        if (!keepKeys.includes(key))
        {
          delete this.__proxy[key];
        }
      });

      // Assign new data
      Object.assign(this.__proxy, data);

      return this.__proxy;
    });

  }

  /**
   * Clear all data
   */
  empty()
  {
    return this.replace({});
  }

  /**
   * Clean up
   */
  destroy()
  {
    this.__subscribers = null;
    this.__parent = null;
    this.__proxy = null;
    this.__batchEvents = null;
  }
}

/**
 * WDModelArray - Reactive array proxy
 * 
 * This is a proxy wrapper around a native JavaScript array.
 * All native array methods work automatically and trigger events.
 */
export class WDModelArray extends WDModel
{

  constructor(arr = [], options = {})
  {
    // Ensure we have an array
    if (!Array.isArray(arr))
    {
      arr = [];
    }

    // Call parent constructor with array
    super(arr, options);

    // The parent already returns the proxy, so we return it too
    return this.__proxy;
  }

  /**
   * Proxy trap: filter array keys properly
   */
  ownKeys(target)
  {
    return Reflect.ownKeys(target).filter(key =>
    {
      // Allow length property
      if (key === 'length') return true;

      // Allow numeric indices
      const numKey = +key;
      return !Number.isNaN(numKey) && numKey >= 0;
    });
  }

  /**
   * Convert to plain array
   */
  toArray()
  {
    const result = [];

    this.__proxy.forEach(value =>
    {
      if (value === null)
      {
        result.push(null);
      } else if (typeof value === 'object')
      {
        if (value?.isProxy)
        {
          result.push(Array.isArray(value) ? value.toArray() : value.toObject());
        } else
        {
          result.push(Array.isArray(value) ? [...value] : { ...value });
        }
      } else
      {
        result.push(value);
      }
    });

    return result;
  }

  /**
   * Replace array contents
   */
  replace(newArr)
  {
    if (!Array.isArray(newArr))
    {
      newArr = [];
    }

    return this.batch(() =>
    {
      // Clear existing elements
      while (this.__proxy.length > 0)
      {
        this.__proxy.pop();
      }

      // Add new elements
      newArr.forEach(item =>
      {
        this.__proxy.push(item);
      });

      return this.__proxy;
    });
  }

  /**
   * Reorder elements
   */
  move(fromIndex, toIndex)
  {
    const element = this.__proxy.splice(fromIndex, 1)[0];
    this.__proxy.splice(toIndex, 0, element);
    return this.__proxy;
  }

  /**
   * Remove element at index
   */
  remove(index)
  {
    this.__proxy.splice(index, 1);

    return this.__proxy;
  }

  /**
   * Insert element at index
   */
  insert(index, item)
  {
    this.__proxy.splice(index, 0, item);
    return this.__proxy;
  }
}
